import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Box } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useRegion, useRegionSearch } from "../cspServices";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { isBlank, notBlank } from "../../util/ext";
import { equals, find, propEq } from "ramda";
import TextField from "@material-ui/core/TextField";
import { FixedSizeList } from "react-window";
import { useDebounce } from "../../util/hooks";

const LISTBOX_PADDING = 4; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 50;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemSize * itemCount;
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          style={{
            borderStyle: "solid",
            borderColor: "#F7F9FA",
            borderWidth: 2,
          }}
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const RegionSelect = props => {
  const { onChange, value, error = false, helperText } = props;
  const [open, setOpen] = useState(false);

  const [searchString, setSearchString] = useState("");
  const debouncedSearchTerm = useDebounce(searchString, 500);

  const [selectedRegion, setSelectedRegion] = useState(null);
  const regions = useRegionSearch(debouncedSearchTerm);
  const [regionList, setRegionList] = useState([]);

  const region = useRegion(value);
  useEffect(() => {
    if (region.isSuccess) {
      setSearchString(region.data.name);
    }
  }, [region.isSuccess, region.data, setSearchString]);

  useEffect(() => {
    if (regions.isSuccess) {
      setRegionList(regions.data);
      setSelectedRegion(find(propEq("code", value), regions.data));
    } else {
      setRegionList([]);
      setSelectedRegion(null);
    }
  }, [
    regions.isSuccess,
    regions.data,
    value,
    setOpen,
    setRegionList,
    setSelectedRegion,
    searchString,
  ]);

  const handleRegionSelect = useCallback(
    (_event, region) => {
      setSelectedRegion(region);
      onChange(region ? region.code : null);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (event, value, reason) => {
      if (reason === "input") {
        notBlank(value) ? setSearchString(value) : setSearchString("");
        setOpen(true);
      } else if (reason === "clear") {
        setRegionList([]);
        setSearchString("");
        setOpen(true);
      }
    },
    [setSearchString],
  );

  return (
    <Fragment>
      {regions.isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <Autocomplete
          disableListWrap
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          selectOnFocus
          handleHomeEndKeys
          options={regionList}
          value={selectedRegion || null}
          inputValue={
            isBlank(selectedRegion) ? searchString : selectedRegion.name
          }
          ListboxComponent={ListboxComponent}
          getOptionLabel={option => notBlank(option) && `${option.name}`}
          getOptionSelected={(opt, val) =>
            notBlank(val) && equals(opt.code, val.code)
          }
          renderOption={(option, { selected }) => (
            <Box>
              <Box fontSize="1.4rem">{option.name}</Box>
              <Box fontSize="1.2rem">{`(${option.countryCode})`}</Box>
            </Box>
          )}
          onChange={handleRegionSelect}
          onInputChange={handleInputChange}
          renderInput={params => {
            params.inputProps["aria-autocomplete"] = "none";
            params.inputProps["id"] = null;
            return (
              <TextField
                {...params}
                type="text"
                name={"region-search-" + Math.random()}
                variant="standard"
                label="Region"
                value={searchString}
                error={error}
                helperText={helperText}
                autoFocus={notBlank(searchString) || open}
                fullWidth
              />
            );
          }}
        />
      )}
    </Fragment>
  );
};

export default RegionSelect;
